<template>
  <div
    class="interactive-slider"
    :class="{'interactive-slider--push': activeSubBottomBar.length > 8}"
  >
    <transition-group
      class="interactive-slider__slides"
      @before-enter="beforeEnter"
      @enter="enter"
      @afterEnter="afterEnter"
      @beforeLeave="beforeLeave"
      @leave="leave"
      @afterLeave="afterLeave"
      :css="true"
    >      
      <div
        v-for="(slide, index) in activeSlides"
        class="interactive-slider__slide"
        :class="[
          { 'interactive-slider__slide--active' : activeSlideId === slide._id },
          { 'interactive-slider__slide--active' : activeSubsectionId ? activeSubsectionId === slide._id : false },
          { 'interactive-slider__slide--inactive' : activeSlideId !== null && activeSlideId !== slide._id }, 
          { 'interactive-slider__slide--expand' : activeSlideId === slide._id && slide.shouldSlideExpand },
          { 'interactive-slider__slide--sold-out' : showSoldLabel(slide) },
          { 'touch' : isClickable(slide) }
        ]"
        :data-index="index"
        :key="slide._id"
        @click="setActiveSlideId(slide._id)"
      >
        <div v-if="slide.label && !slide.label.en" class="interactive-slider__label" v-html="slide.label"></div>
        <div v-if="slide.label && slide.label.en" class="interactive-slider__label" v-html="slide.label.en"></div>
        <video
          v-if="getAssetType(slide) === 'video'"
          :id="`video-${slide._id}`"
          class="interactive-slider__video"
          :src="getBackgroundUrl(slide)"
          autoplay
          loop
          muted
        />
        <img v-if="getAssetType(slide) === 'image'" :src="getBackgroundUrl(slide)">
      </div>
    </transition-group>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
import { bus } from '../main'

export default {
  props: ['slides'],

  data () {
    return {
      activeSlides: [],
      activeSlide: null,
      activeSlideId: null
    }
  },

  computed: {
    ...mapGetters([
      'activePage',
      'activeSubBottomBar',
      'language',
      'styleTemplate',
    ]),

    activeSubsectionId () {
      return this.$store.state.route.query.activeSubsectionId
    },
  },

  watch: {
    slides (newSlides, oldSlides) {
      if (!_.isEqual(oldSlides, newSlides)) {
        if (oldSlides.length) {
          this.activeSlides = []
          this.activeSlideId = null
        }

        if (newSlides.length) {
          this.$store.commit('addAnimationToQueue', 'Slides')
        }
      }
    }
  },

  mounted () {
    bus.$on('animateSlides', ()=> {
      if (!_.isEqual(this.slides, this.activeSlides)) {
        setTimeout(() => {
          this.activeSlides = this.slides
        }, 300)
      } else {
        bus.$emit('finishedAnimation')
      }
    }) 
  },

  methods: {
    getAssetType (slide) {
      if (slide.asset) {
        const ext = slide.asset.path.split('.').pop().toLowerCase()
        if (ext === 'mov' || ext === 'mp4') {
          return 'video'
        }

        if (ext === 'jpeg' || ext === 'jpg' || ext === 'png' || ext === 'gif') {
          return 'image'
        }

        return 'unsupported'
      }

      return slide.cardBackgroundImage || slide.backgroundImage ? 'image' : ''
    
    },

    getBackgroundUrl (slide) {
      if (slide.asset) {
        return this.$store.state.assetsPath + slide.asset.path
      }

      if (slide.cardBackgroundImage || slide.backgroundImage) {
        return slide.cardBackgroundImage ? this.$store.state.assetsPath + slide.cardBackgroundImage : this.$store.state.assetsPath + slide.backgroundImage
      }
    },

    setActiveSlideId (id) {
      if (this.activeSlideId === id) {
        this.activeSlide = null
        this.activeSlideId = null
        this.$store.commit('SET_ACTIVE_TAGS_OVERRIDE', null)
        this.$store.commit('SET_ACTIVE_BACKGROUND_OVERRIDE', '')
        setTimeout(() => {
          bus.$emit('startAnimations')
        }, 500)

      } else if (this.activeSubsectionId === id) {
        this.navTo(id)

      } else {
        const slide = _.find(this.slides, slide => {
          return slide._id === id
        })

        // check slide type
        const isLivingOption = slide.livingOptionId !== undefined || slide.subsections !== undefined

        if (isLivingOption) {
          this.navTo(id)
        } else {
          this.activeSlide = slide
          this.activeSlideId = id

          // set slide tags 
          const tags = _.sortBy(slide.tags, tag => { 
            return tag.pos.x
          })
          this.$store.commit('SET_ACTIVE_TAGS_OVERRIDE', tags)

          // set background
          this.$store.commit('SET_ACTIVE_BACKGROUND_OVERRIDE', slide.backgroundImageOverride)

          setTimeout(() => {
            bus.$emit('startAnimations')
          }, 500)
          
          if (slide.backgroundImageOverride) {
            this.$store.state.animations.animateBackground = this.$store.state.assetsPath + slide.backgroundImageOverride
          }
        }
      }
    },

    navTo (id) {
      const categoryId = this.$route.params.categoryId
      const livingOptionId = this.$route.params.livingOptionId
      const subsectionId = this.$route.params.subsectionId
      const pathway = this.$route.params.pathway

      const toLivingOption = this.$store.getters['property/livingOptions'].find(livingOption => livingOption._id === id)
      const toSubsection = this.$store.getters['property/subsections'].find(subsection => subsection._id === id)

      if (toLivingOption) {
        if (livingOptionId === id) {
          this.$router.push(`/category/${categoryId}/live/${pathway}`)
        } else {
          if (toLivingOption.subsections.length === 1) {
            this.$router.push(`/category/${categoryId}/live/${pathway}/${id}/subsection/${toLivingOption.subsections[0]._id}?activeSubsectionId=${toLivingOption.subsections[0]._id}`)
          } else {
            this.$router.push(`/category/${categoryId}/live/${pathway}/${id}`)
          }
        }
      }

      if (toSubsection) {
        if (subsectionId === id) {
          this.$router.push(`/category/${categoryId}/live/${pathway}/${livingOptionId}`)
        } else {
          this.$router.push(`/category/${categoryId}/live/${pathway}/${livingOptionId}/subsection/${id}?activeSubsectionId=${id}`)
        }
      }
    },

    isClickable (slide) {
      if (slide.hasOwnProperty('availableProperties') && slide.availableProperties === 0) {
        return false
      } else {
        return true
      }
    },

    showSoldLabel (slide) {
      return this.styleTemplate['general']['show-sold-label'] && slide.availableProperties === 0
    },

    beforeEnter (el) {
      el.removeAttribute('style')
      el.style.opacity = 0
      el.style.transform = 'translateY(25%)'
    },

    enter (el, done) {
      setTimeout(() => {
        el.style.opacity = 1
        el.style.transform = 'translateY(0)'
        el.style.transition = `transform 0.6s cubic-bezier(0.33, 1, 0.68, 1), opacity 1.2s`
        setTimeout(() => {
          done()
        }, 300)
      }, (parseInt(el.dataset.index) + 1) * 150)
    },

    afterEnter (el) {
      if (parseInt(el.dataset.index) + 1 === this.slides.length) {
        bus.$emit('finishedAnimation')
      }      
      el.removeAttribute('style')
    },

    beforeLeave (el) {
      el.style.transition = `transform 0.3s cubic-bezier(0.32, 0, 0.67, 0), opacity 0.3s`
    },

    leave (el) {
      el.style.opacity = 0
      el.style.transform = 'translateY(25%)'
    },

    afterLeave (el) {
      el.removeAttribute('style')
    }
  },
}
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
$transition-speed: 300ms;

.interactive-slider {
  bottom: 16rem;
  left: 0;
  position: absolute;
  right: 32.5rem;
  z-index: 20;

  &--push {
    bottom: 19rem;
  }

  &__slides {
    align-items: flex-end;
    display: flex;
    justify-content: center;
  }
  
  &__slide {
    border-radius: .5rem;
    box-shadow: rgba($black, 0.6) 0.1rem 0.2rem 0.4rem;
    flex-shrink: 0;
    height: 8.25rem;
    width: 12.5rem;
    margin: 0 .6rem;
    overflow: hidden;
    position: relative;
    transition: all $transition-speed ease-in-out;

    &--active {
      box-shadow:  0 0 .5rem .1rem rgb(255, 255, 255);
      opacity: 1;
    }

    &--expand {
      box-shadow: rgba($black, 0.6) 0.1rem 0.2rem 0.4rem;
      margin: 0 1rem;
      height: calc(8.25rem * 2.5);
      width: calc(12.5rem * 2.5);
    }

    &--sold-out {
      pointer-events: none !important;
      position: relative;

      &::before {
        background: #e90303;
        border-radius: .4rem;
        color: #fff;
        content: 'sold out';
        font-size: 1rem;
        font-weight: 900;
        overflow: hidden;
        padding: .1rem .5rem .05rem;
        position: absolute;
        left: 50%;
        letter-spacing: .1em;
        text-transform: uppercase;
        transform: rotate(10deg) translate(-50%, -50%);
        transform-origin: left;
        top: 50%;
        width: max-content;
        z-index: 10;
      }
    }

    img {
      position: absolute;
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  }

  &__video {
    position: absolute;
    height: 100%;
    object-fit: cover;
    width: 100%;

    &-poster {
      transition: opacity 0.3s;

      .interactive-slider__slide--active & {
        opacity: 0;
      }
    }
  }

  &__play-icon {
    font-size: 2rem;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }

  &__label {
    align-items: center;
    background: rgba(0, 0, 0, .6);
    bottom: 0;
    display: flex;
    font-size: .6rem;
    font-weight: 300;
    justify-content: center;
    height: 2.1rem;
    letter-spacing: .05rem;
    padding: .2rem .75rem;
    position: absolute;
    text-align: center;
    text-transform: uppercase;
    width: 100%;
    z-index: 3;
  }
}
</style>